import { api } from "@/api/base-api"
import type { HarnessPaymentMethodBillingAddress } from "@/api/providers/payment-methods"

export type HarnessInvoiceStatus = "Draft" | "Open" | "Paid" | "Voided";
export type HarnessInvoiceStatusDetails =
  | "PartiallyPaid"
  | "PartiallyRefunded"
  | "Refunded"
  | "PaymentProcessing";
export type HarnessPaymentStatus =
  | "Unpaid"
  | "PartiallyPaid"
  | "Paid"
  | "Refunded"
  | "PartiallyRefunded";

export type HarnessInvoice = {
  id: string | null;
  subscriptionId: string | null;
  subscriptionName: string | null;
  number: string | null;
  invoiceNumber: string | null;
  title: string | null;
  invoiceStatus: HarnessInvoiceStatus;
  invoiceStatusDetails: HarnessInvoiceStatusDetails;
  paymentStatus: HarnessPaymentStatus;
  invoiceDate: string | null;
  dueDate: string | null;
  paymentDate: string | null;
  amountBilled: number | null;
  amountPaid: number | null;
  balanceDue: number | null;
  amountBilledInCompanyCurrency: number | null;
  amountPaidInCompanyCurrency: number | null;
  balanceDueInCompanyCurrency: number | null;
  currency: string | null;
  createdDate: string | null;
};

export type GetHarnessInvoicesQueryArgs = {
  customerId: number;
  subscriptionIds?: string[];
  invoiceStatus?: HarnessInvoiceStatus[];
  dueDateFrom?: string;
  dueDateTo?: string;
};

export enum InvoicePaymentMethodSourceType {
  NewPaymentMethod = "NewPaymentMethod",
  SavedPaymentMethod = "SavedPaymentMethod",
}

export enum InvoicePaymentInstrumentKind {
  Card = "Card",
  Ach = "ACH",
}

export type HarnessInvoicePaymentExecutionStatus =
  | "Success"
  | "SuccessWithWarnings"
  | "GuardrailDenied"
  | "ValidationFailed"
  | "InvoiceNotPayable"
  | "PaymentFailed"
  | "HubSpotUpdateFailedAndVoided"
  | "HubSpotUpdateFailedVoidFailed"
  | "UnexpectedFailure";

export type HarnessPayInvoicePaymentMethod = {
  sourceType: InvoicePaymentMethodSourceType
  customerPaymentMethodId?: number | null
  instrumentKind: InvoicePaymentInstrumentKind
  paymentToken?: string | null
  maskedAccountNumber?: string | null
  expirationDate?: string | null
  paymentMethodNickname?: string | null
  achAccountType?: string | null
  achAccountHolderType?: string | null
  cardType?: string | null
  cardCategory?: string | null
  savePaymentMethod: boolean
  makeDefaultPaymentMethod: boolean
  billingAddress?: HarnessPaymentMethodBillingAddress
}

export type HarnessPayInvoiceRequestBody = {
  paymentMethod: HarnessPayInvoicePaymentMethod;
  invoiceAmount?: number | null;
  totalAmount: number;
  surchargeAmount?: number | null;
};

export type HarnessPayInvoiceResponse = {
  isSuccess: boolean;
  status: HarnessInvoicePaymentExecutionStatus;
  cardType: string | null;
  cardCategory: string | null;
  isSurchargeEligible: boolean;
  invoicePaymentTransactionId: number | null;
  paymentTransactionId: string | null;
  responseCode: string | null;
  responseMessage: string | null;
  errorMessage: string | null;
  warningMessage: string | null;
  hubSpotStatusUpdated: boolean;
  wasVoidAttempted: boolean;
  isVoidSuccessful: boolean;
  savedPaymentMethodCreated: boolean;
  savedCustomerPaymentMethodId: number | null;
};

export type GetHarnessInvoiceQueryArgs = {
  customerId: number;
  invoiceId: string;
};

export type GetHarnessInvoiceHtmlQueryArgs = GetHarnessInvoiceQueryArgs;
export type GetHarnessInvoiceReceiptHtmlQueryArgs = GetHarnessInvoiceQueryArgs;

export type HarnessInvoicePdfSizing = {
  letterWidthPx?: number;
  logoLockWidthPx?: number;
  logoLockHeightPx?: number;
};

export type HarnessInvoiceHtmlResponse = {
  html: string;
  pdfSizing: HarnessInvoicePdfSizing;
};

export type PayHarnessInvoiceArgs = {
  customerId: number;
  invoiceId: string;
  body: HarnessPayInvoiceRequestBody;
};

function parsePositiveHeaderNumber(value: string | null) {
  if (!value) {
    return undefined;
  }

  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

const invoicesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getHarnessInvoices: builder.query<
      HarnessInvoice[],
      GetHarnessInvoicesQueryArgs
    >({
      query: ({ customerId, ...params }) => ({
        url: `/api/providers/Harness/CloudFitPortal/Customer/${customerId}/Invoice`,
        params,
      }),
    }),
    getHarnessInvoice: builder.query<
      HarnessInvoice,
      GetHarnessInvoiceQueryArgs
    >({
      query: ({ customerId, invoiceId }) => ({
        url: `/api/providers/Harness/CloudFitPortal/Customer/${customerId}/Invoice/${invoiceId}`,
      }),
    }),
    getHarnessInvoiceHtml: builder.query<
      HarnessInvoiceHtmlResponse,
      GetHarnessInvoiceHtmlQueryArgs
    >({
      query: ({ customerId, invoiceId }) => ({
        url: `/api/providers/Harness/CloudFitPortal/Customer/${customerId}/Invoice/${invoiceId}/html`,
        responseHandler: (response) => response.text(),
      }),
      transformResponse: (response: string, meta) => {
        const headers = meta?.response?.headers;

        return {
          html: response,
          pdfSizing: {
            letterWidthPx: parsePositiveHeaderNumber(
              headers?.get("X-CloudFit-Invoice-Pdf-Letter-Width-Px") ?? null,
            ),
            logoLockWidthPx: parsePositiveHeaderNumber(
              headers?.get("X-CloudFit-Invoice-Pdf-Logo-Lock-Width-Px") ?? null,
            ),
            logoLockHeightPx: parsePositiveHeaderNumber(
              headers?.get("X-CloudFit-Invoice-Pdf-Logo-Lock-Height-Px") ??
                null,
            ),
          },
        };
      },
    }),
    getHarnessInvoiceReceiptHtml: builder.query<
      HarnessInvoiceHtmlResponse,
      GetHarnessInvoiceReceiptHtmlQueryArgs
    >({
      query: ({ customerId, invoiceId }) => ({
        url: `/api/providers/Harness/CloudFitPortal/Customer/${customerId}/Invoice/${invoiceId}/receipt`,
        responseHandler: (response) => response.text(),
      }),
      transformResponse: (response: string, meta) => {
        const headers = meta?.response?.headers;

        return {
          html: response,
          pdfSizing: {
            letterWidthPx: parsePositiveHeaderNumber(
              headers?.get("X-CloudFit-Invoice-Pdf-Letter-Width-Px") ?? null,
            ),
            logoLockWidthPx: parsePositiveHeaderNumber(
              headers?.get("X-CloudFit-Invoice-Pdf-Logo-Lock-Width-Px") ?? null,
            ),
            logoLockHeightPx: parsePositiveHeaderNumber(
              headers?.get("X-CloudFit-Invoice-Pdf-Logo-Lock-Height-Px") ??
                null,
            ),
          },
        };
      },
    }),
    payHarnessInvoice: builder.mutation<
      HarnessPayInvoiceResponse,
      PayHarnessInvoiceArgs
    >({
      query: ({ customerId, invoiceId, body }) => ({
        url: `/api/providers/Harness/CloudFitPortal/Customer/${customerId}/Invoice/${invoiceId}/pay`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetHarnessInvoicesQuery,
  useGetHarnessInvoiceQuery,
  useGetHarnessInvoiceHtmlQuery,
  useGetHarnessInvoiceReceiptHtmlQuery,
  useLazyGetHarnessInvoiceHtmlQuery,
  useLazyGetHarnessInvoiceReceiptHtmlQuery,
  usePayHarnessInvoiceMutation,
} = invoicesApi;
